import { el } from "./dom.js";
import { gameScreen } from "./game.js";
import { CHAT_MAX_LENGTH, TEAM_COLORS, type MatchmadeMode, type MergedWorldSnapshot } from "@webolo/shared";
import type { GameClient } from "../game/client.js";

const TANK_COLOR_KEY = "webolo.tankColor";

// matchLobbyScreen is a thin wrapper around gameScreen() — gameScreen owns
// the WebSocket connection, the canvas, and every in-game HUD piece. Passing
// the matchmadeMode argument flips on the in-screen warmup overlay
// (countdown · chat · ROAM FREE · FFA colour picker) which auto-removes when
// the snapshot's match.phase transitions to "playing". Because we never
// reconnect, the player's pre-match roam position carries straight into the
// live match.
//
// `warmupEndsAt` is passed in for completeness — the overlay reads the
// authoritative value from the snapshot's MatchState as soon as the first
// frame arrives. The argument is kept so the route's HTTP response value can
// be threaded through without an extra round-trip wait.
export function matchLobbyScreen(
  gameId: string,
  team: number,
  warmupEndsAt: number,
  mode: MatchmadeMode,
  onExit: () => void,
): HTMLElement {
  return gameScreen(gameId, team, onExit, mode, warmupEndsAt);
}

export interface WarmupOverlayApi {
  /** Detached node — gameScreen appends it inside the canvas-area wrap. */
  root: HTMLElement;
  /**
   * Fed every snapshot. Returns false once the snapshot reports
   * match.phase === "playing"; gameScreen then removes the overlay
   * permanently.
   */
  update(snap: MergedWorldSnapshot): boolean;
  /** Mirror in-game chat messages into the overlay's chat log. */
  appendChat(from: string, text: string): void;
  /** Called when the overlay is removed; cancel any timers. */
  destroy(): void;
}

// Builds the Match Lobby overlay attached on top of gameScreen's canvas
// during the pre-match warmup. The overlay has four parts:
//
//   1. Countdown timer (read from snap.match.warmupEndsAt every frame).
//   2. Roster (snap.match.warmupRoster — usernames + picked tank colours).
//   3. ROAM FREE button — hides the overlay so the player can drive their
//      indestructible tank around for the rest of the countdown. The
//      overlay still ticks update() in the background so the auto-remove
//      on phase transition still fires.
//   4. FFA-only tank colour picker — clicking a swatch fires setColor on
//      the WS, stashes the choice in localStorage, and updates the
//      roster row in place.
//
// A phase-2 placeholder slot sits below for paid tank skins.
export function warmupOverlay(opts: {
  client: GameClient;
  mode: MatchmadeMode;
  // ms epoch the warmup ends, handed back by /api/matchmake. Used as an
  // immediate seed so the countdown is painted from the first frame rather
  // than waiting for the first snapshot to arrive (which can be 50–500ms
  // after the WebSocket opens).
  initialWarmupEndsAt?: number;
}): WarmupOverlayApi {
  // Battle royale is a free-for-all, so it gets the same colour picker as FFA.
  const isFfa = opts.mode === "ffa" || opts.mode === "super_ffa" || opts.mode === "battle_royale";
  const modeLabel =
    opts.mode === "teams" ? "TEAMS" :
    opts.mode === "battle_royale" ? "BATTLE ROYALE" :
    "FREE FOR ALL";
  const isMounted = { value: true };
  let timer = 0;
  let lastWarmupEndsAt = opts.initialWarmupEndsAt && opts.initialWarmupEndsAt > 0
    ? opts.initialWarmupEndsAt
    : 0;

  const countdownEl = el("span", { class: "ml-countdown" }, "—");
  const rosterEl = el("ul", { class: "ml-roster" });
  // ENTER BATTLE — also visible as a small countdown chip that stays on
  // screen after the player taps it (the main overlay hides but the chip
  // anchors the warmup deadline to the corner so the player knows when
  // damage actually goes live).
  const chipCountdown = el("span", { class: "ml-chip-count" }, "—");
  const chip = el("div", { class: "ml-chip hidden" },
    el("span", { class: "ml-chip-lbl" }, "MATCH IN"),
    chipCountdown,
  );

  const chatLog = el("div", { class: "ml-chat-log" });
  const chatInput = el("input", {
    type: "text",
    class: "ml-chat-input",
    maxlength: CHAT_MAX_LENGTH,
    placeholder: "Say hi to your match…",
  }) as HTMLInputElement;
  chatInput.addEventListener("keydown", (e) => {
    if (e.key !== "Enter") return;
    const text = chatInput.value.trim();
    if (!text) return;
    opts.client.sendChat(text);
    chatInput.value = "";
  });

  const overlay = el("div", { class: "ml-card" });
  // The overlay wrap holds both the main card (visible while the player is
  // still picking colour / chatting) and a corner chip (visible after the
  // player taps ENTER BATTLE and roams the map). The chip auto-removes when
  // the overlay does on the warmup → playing transition.
  const overlayWrap = el("div", { class: "ml-overlay" }, overlay, chip);

  // ── Header row: countdown + ENTER BATTLE
  const roamBtn = el(
    "button",
    {
      class: "ml-roam",
      type: "button",
      onclick: () => {
        // Hide the main card + clear the overlay scrim so the player can
        // drive their tank, and surface the corner chip so the countdown
        // stays visible. update() keeps running so the live phase
        // transition still triggers permanent removal of the whole
        // overlayWrap (both card and chip).
        overlay.classList.add("hidden");
        overlayWrap.classList.add("ml-overlay-roam");
        chip.classList.remove("hidden");
      },
    },
    "ENTER BATTLE",
  );
  // Centred siege-style countdown box: mode label on top, big numeric
  // countdown below — same visual language as the in-game siege HUD so
  // the timer feels consistent across modes.
  overlay.append(
    el("div", { class: "ml-head" },
      el("div", { class: "ml-mode" }, modeLabel),
      el("div", { class: "ml-sub" }, "MATCH STARTING IN"),
      countdownEl,
    ),
  );
  overlay.append(el("div", { class: "ml-roam-row" }, roamBtn));

  // ── Roster
  overlay.append(
    el("section", { class: "ml-section" },
      el("h3", { class: "ml-section-ttl" }, "IN THE LOBBY"),
      rosterEl,
    ),
  );

  // ── Tank colour picker (FFA only)
  let picked = loadTankColor();
  const swatchRow = el("div", { class: "ml-swatches" });
  if (isFfa) {
    for (let i = 0; i < TEAM_COLORS.length; i++) {
      const swatch = el(
        "button",
        {
          type: "button",
          class: "ml-swatch",
          style: `background: ${TEAM_COLORS[i]}`,
          title: `Colour ${i}`,
          onclick: () => {
            picked = i;
            saveTankColor(i);
            opts.client.sendSetColor(i);
            refreshSwatchSelection();
          },
        },
      );
      swatchRow.append(swatch);
    }
    overlay.append(
      el("section", { class: "ml-section" },
        el("h3", { class: "ml-section-ttl" }, "TANK COLOUR"),
        swatchRow,
      ),
    );
    // Push the user's saved colour to the server as soon as the WS settles.
    // If the connection isn't open yet, the client.send() no-ops; the user
    // can re-click any swatch to retry.
    if (picked !== null) {
      // Defer one tick so client.onOpen has already fired in the common case.
      setTimeout(() => { if (picked !== null) opts.client.sendSetColor(picked); }, 50);
    }
    refreshSwatchSelection();
  }

  // ── Chat
  overlay.append(
    el("section", { class: "ml-section ml-chat-section" },
      el("h3", { class: "ml-section-ttl" }, "CHAT"),
      chatLog,
      chatInput,
    ),
  );

  function refreshSwatchSelection(): void {
    const children = Array.from(swatchRow.children) as HTMLElement[];
    for (let i = 0; i < children.length; i++) {
      children[i].classList.toggle("selected", i === picked);
    }
  }

  function renderRoster(snap: MergedWorldSnapshot): void {
    const roster = snap.match.warmupRoster ?? [];
    rosterEl.replaceChildren(
      ...roster.map((r) => {
        const color = r.colorIndex !== undefined ? TEAM_COLORS[r.colorIndex] : "#5f6470";
        return el("li", { class: "ml-roster-row" },
          el("span", { class: "ml-roster-dot", style: `background: ${color}` }),
          el("span", { class: "ml-roster-name" }, r.username),
        );
      }),
    );
    if (roster.length === 0) {
      rosterEl.append(el("li", { class: "ml-roster-empty" }, "Waiting for players…"));
    }
  }

  function paintCountdown(): void {
    if (lastWarmupEndsAt <= 0) return;
    const sec = Math.max(0, Math.ceil((lastWarmupEndsAt - Date.now()) / 1000));
    const text = `${sec}s`;
    countdownEl.textContent = text;
    chipCountdown.textContent = text;
  }

  function update(snap: MergedWorldSnapshot): boolean {
    if (snap.match.phase !== "warmup") return false;
    // Only overwrite the seeded value when the server hands back a real
    // (non-zero) warmupEndsAt. Some server paths can emit 0 transiently
    // (e.g. fresh room before the warmup state has propagated through the
    // first snapshot loop) and we don't want the counter to blank out.
    const snapEnd = snap.match.warmupEndsAt ?? 0;
    if (snapEnd > 0) lastWarmupEndsAt = snapEnd;
    paintCountdown();
    renderRoster(snap);
    return true;
  }

  // Tick the countdown locally between server snapshots so the number visibly
  // counts down at the player's display rate (server pushes 20Hz, but the
  // wall-clock display should still feel immediate). Paint once immediately
  // so the HTTP-seeded value lands on screen before the first snapshot.
  paintCountdown();
  timer = window.setInterval(() => {
    if (!isMounted.value) return;
    paintCountdown();
  }, 200);

  function appendChat(from: string, text: string): void {
    const row = el("div", { class: "ml-chat-row" },
      el("span", { class: "ml-chat-from" }, from),
      document.createTextNode(" "),
      el("span", { class: "ml-chat-text" }, text),
    );
    chatLog.append(row);
    while (chatLog.children.length > 60) chatLog.firstElementChild?.remove();
    chatLog.scrollTop = chatLog.scrollHeight;
  }

  function destroy(): void {
    isMounted.value = false;
    if (timer) window.clearInterval(timer);
  }

  return { root: overlayWrap, update, appendChat, destroy };
}

function loadTankColor(): number | null {
  try {
    const raw = localStorage.getItem(TANK_COLOR_KEY);
    if (raw == null) return null;
    const n = Number.parseInt(raw, 10);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function saveTankColor(i: number): void {
  try { localStorage.setItem(TANK_COLOR_KEY, String(i)); } catch { /* ignore */ }
}
